import {
  BarChart3,
  CreditCard,
  Info,
  LogOut,
  MessageCircle,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuTrigger,
} from '@repo/ui/menu';

import { useIsAdmin } from '../admin/admin-route';
import { trackEvent } from '../analytics/posthog';
import { WHATSAPP_AVAILABLE } from '../whatsapp/whatsapp-available';
import { useAuth } from './use-auth';

/**
 * Account control in the top-right corner: the initial in a yellow circle,
 * opening a menu with who you are and where to go. The email lives inside the
 * menu rather than on the page, so the screen keeps a single loud element
 * (/design.md, visual weight).
 *
 * Laid out as the Floto Rooms design has it — name and email over a hairline,
 * then one row per place, each with its icon.
 */
function Item({
  children,
  icon,
  onClick,
}: {
  children: ReactNode;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <MenuItem
      className="h-11 cursor-pointer gap-2.5 !rounded-button px-3 py-0 text-ui-lg font-medium text-text-primary data-highlighted:bg-surface-hover [&_svg]:text-text-secondary"
      onClick={onClick}
    >
      {icon}
      {children}
    </MenuItem>
  );
}

export function UserMenu() {
  const { signOut, user } = useAuth();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  if (!user) return null;

  const label = user.displayName ?? user.email ?? '?';
  const initial = [...label][0]?.toUpperCase() ?? '?';

  return (
    <Menu
      // Not modal: a modal menu locks the page's scroll, which takes the
      // scrollbar away and shifts everything behind it sideways.
      modal={false}
      onOpenChange={(open) => {
        if (open) trackEvent('user_menu_opened');
      }}
    >
      <MenuTrigger
        aria-label={`Account menu for ${label}`}
        className="size-10.5 cursor-pointer rounded-pill bg-sunny font-heading text-ui-lg font-bold text-text-primary transition-transform hover:scale-105 active:scale-[0.98]"
      >
        {initial}
      </MenuTrigger>

      <MenuContent className="w-[276px] !rounded-room border-0 bg-surface p-2 !shadow-menu">
        <MenuLabel className="flex flex-col gap-0.5 border-b border-hairline px-3 pt-2.5 pb-3">
          {user.displayName ? (
            <span className="text-subtitle font-bold text-text-primary">
              {user.displayName}
            </span>
          ) : null}
          {user.email ? (
            <span className="text-ui text-text-muted">{user.email}</span>
          ) : null}
        </MenuLabel>

        <div className="pt-1.5">
          {WHATSAPP_AVAILABLE && (
            <Item
              icon={<MessageCircle aria-hidden="true" />}
              onClick={() => {
                trackEvent('user_menu_item_clicked', { item: 'whatsapp' });
                void navigate('/whatsapp');
              }}
            >
              WhatsApp
            </Item>
          )}

          <Item
            icon={<CreditCard aria-hidden="true" />}
            onClick={() => {
              trackEvent('user_menu_item_clicked', { item: 'billing' });
              void navigate('/billing');
            }}
          >
            Plans &amp; billing
          </Item>

          <Item
            icon={<BarChart3 aria-hidden="true" />}
            onClick={() => {
              trackEvent('user_menu_item_clicked', { item: 'usage' });
              void navigate('/billing/usage');
            }}
          >
            Credit usage
          </Item>

          {/* The only way into /admin from the product. Rendered from the
              server's own answer (`/admin/whoami`), so there is no client-side
              copy of the domain rule to drift — and the guard refuses the
              routes regardless of what this decides. */}
          {isAdmin ? (
            <Item
              icon={<ShieldCheck aria-hidden="true" />}
              onClick={() => {
                trackEvent('user_menu_item_clicked', { item: 'admin' });
                void navigate('/admin');
              }}
            >
              Admin
            </Item>
          ) : null}

          <Item
            icon={<Info aria-hidden="true" />}
            onClick={() => {
              trackEvent('user_menu_item_clicked', { item: 'support' });
              void navigate('/support');
            }}
          >
            Contact support
          </Item>

          <Item
            icon={<LogOut aria-hidden="true" />}
            onClick={() => {
              void signOut();
            }}
          >
            Sign out
          </Item>
        </div>
      </MenuContent>
    </Menu>
  );
}
